import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import { componentScope } from '../../lib/cssScope'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'
import { TextField } from '../ui/TextField'
import shellStyles from './SupplierFormModal.module.css'
import styles from './AddIncidentModal.module.css'

const INCIDENT_TYPE_OPTIONS = [
  { value: 'DATA_BREACH', label: 'Data Breach' },
  { value: 'SECURITY_INCIDENT', label: 'Security Incident' },
  { value: 'COMPLIANCE_VIOLATION', label: 'Compliance Violation' },
  { value: 'SERVICE_DISRUPTION', label: 'Service Disruption' },
  { value: 'VULNERABILITY_DISCLOSURE', label: 'Vulnerability Disclosure' },
] as const

const SEVERITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
] as const

interface AddIncidentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const AddIncidentModal: React.FC<AddIncidentModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [formData, setFormData] = useState({
    supplierId: '',
    type: 'DATA_BREACH',
    severity: 'MEDIUM',
    description: '',
    occurredAt: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    if (isOpen) {
      fetchSuppliers()
    }
  }, [isOpen, token])

  const fetchSuppliers = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.SUPPLIERS, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : [])
    } catch (e) {
      console.error('Failed to fetch suppliers:', e)
    }
  }

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!formData.supplierId.trim()) {
      setError(t('tpsm.incidents.selectSupplier', 'Select a supplier'))
      return
    }
    setLoading(true)

    try {
      const res = await fetch(API_ENDPOINTS.TPSM_INCIDENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          occurredAt: new Date(formData.occurredAt).toISOString()
        })
      })

      const data = await res.json()
      
      if (res.ok) {
        onSuccess()
        onClose()
        setFormData({
          supplierId: '',
          type: 'DATA_BREACH',
          severity: 'MEDIUM',
          description: '',
          occurredAt: new Date().toISOString().split('T')[0]
        })
      } else {
        setError(data.message || 'Failed to create incident')
      }
    } catch (e: any) {
      setError(e.message || 'Failed to create incident')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className={`${componentScope('AddIncidentModal')} ${shellStyles.backdrop}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={shellStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-incident-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={shellStyles.header}>
          <h2 id="add-incident-modal-title" className={shellStyles.title}>
            {t('tpsm.incidents.newIncident', 'New Incident')}
          </h2>
        </div>

        <form
          onSubmit={handleSubmit}
          className={`${shellStyles.form} ${styles.formLoose}`.trim()}
        >
          {error && (
            <div className={shellStyles.error} role="alert">
              {error}
            </div>
          )}

          <Select<string>
            label={
              <>
                {t('tpsm.incidents.supplier', 'Supplier')} <span aria-hidden>*</span>
              </>
            }
            placeholder={t('tpsm.incidents.selectSupplier', 'Select a supplier')}
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            value={formData.supplierId || null}
            onChange={(v) => setFormData({ ...formData, supplierId: v ?? '' })}
            minWidth="100%"
          />

          <div className={shellStyles.grid2}>
            <Select<string>
              label={
                <>
                  {t('tpsm.incidents.type', 'Type')} <span aria-hidden>*</span>
                </>
              }
              options={[...INCIDENT_TYPE_OPTIONS]}
              value={formData.type || null}
              onChange={(v) => setFormData({ ...formData, type: v ?? 'DATA_BREACH' })}
              minWidth="100%"
            />
            <Select<string>
              label={
                <>
                  {t('tpsm.incidents.severity', 'Severity')} <span aria-hidden>*</span>
                </>
              }
              options={[...SEVERITY_OPTIONS]}
              value={formData.severity || null}
              onChange={(v) => setFormData({ ...formData, severity: v ?? 'MEDIUM' })}
              minWidth="100%"
            />
          </div>

          <TextField
            label={
              <>
                {t('tpsm.incidents.occurredAt', 'Occurred At')} <span aria-hidden>*</span>
              </>
            }
            type="date"
            required
            value={formData.occurredAt}
            onChange={(v) => setFormData({ ...formData, occurredAt: v })}
          />

          <TextField
            label={t('tpsm.incidents.description', 'Description')}
            multiline
            rows={4}
            value={formData.description}
            onChange={(v) => setFormData({ ...formData, description: v })}
            placeholder={t('tpsm.incidents.descriptionPlaceholder', 'Describe the incident...')}
          />

          <div className={shellStyles.footer}>
            <Button
              type="button"
              color="outline"
              onClick={onClose}
              disabled={loading}
              className={styles.cancelBtn}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" color="primary" disabled={loading}>
              {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddIncidentModal
